import { AxiosResponse } from 'axios';
import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { KitOrderConfirmation, ResolvedPaymentSource } from '../../api/types';
import { SimplyTestApi } from '../../api/simplytest';
import { selectConfig } from '../../shared/redux/slice';
import { IConfiguration } from '../../shared/types';
import { selectIdToken, selectUserOrgId, unauthorized } from '../../auth/redux/slice';
import { extractErrorMessage } from '../../shared/utils';
import { toast } from 'react-hot-toast';
import kitOrdersSlice, { createKitOrder, loadOrgStripeBilling } from './slice';

export abstract class KitOrdersSagas {
    public static* run(){
        try {
            yield all([
                takeLatest(loadOrgStripeBilling, KitOrdersSagas.loadOrgStripeBilling),
                takeLatest(createKitOrder, KitOrdersSagas.createKitOrder),
            ]);
        }
        catch(e: any){
            console.log(`[KitOrdersSagas:run] the following exception has occurred`, e);
        }
    }

    private static* loadOrgStripeBilling(action: ReturnType<typeof loadOrgStripeBilling>){
        try {
            yield put(kitOrdersSlice.actions.setBillingState('BUSY'));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            const ownOrgId: number = yield select(selectUserOrgId);
            const orgId = action.payload ?? ownOrgId;

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            // Aug 2026 - resolves Enterprise vs the org's own billing (see ResolvedPaymentSource) -
            // fixes the payment step showing "No payment method on file yet" for Enterprise-billed
            // orgs, since getOrganizationStripeBilling only ever looks at the org's own row.
            const billingResponse: AxiosResponse<ResolvedPaymentSource> = yield call(
                { context: client, fn: client.getResolvedPaymentSource },
                orgId,
            );

            if(billingResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(billingResponse.status !== 200)
                throw new Error("Error communicating with Api");

            yield put(kitOrdersSlice.actions.setOrgStripeBilling(billingResponse.data));
            yield put(kitOrdersSlice.actions.setBillingState('READY'));
        }
        catch(e: any){
            toast.error('Error loading payment method');
            console.log(`[KitOrdersSagas:loadOrgStripeBilling] the following exception has occurred`, e);
        }
    }

    private static* createKitOrder(action: ReturnType<typeof createKitOrder>){
        try {
            yield put(kitOrdersSlice.actions.setState('BUSY'));
            yield put(kitOrdersSlice.actions.setErrorMessage(undefined));
            const config: IConfiguration = yield select(selectConfig);
            const idToken: string = yield select(selectIdToken);
            const { payload } = action;

            var client = new SimplyTestApi(config.apiBaseUrl, idToken);
            const orderResponse: AxiosResponse<KitOrderConfirmation> = yield call(
                { context: client, fn: client.createKitOrder },
                { quantity: payload.quantity, orgId: payload.orgId },
            );

            if(orderResponse.status === 401)
            {
                yield put(unauthorized(undefined));
                return;
            }

            if(orderResponse.status !== 200){
                const message = extractErrorMessage(orderResponse.data, 'Error placing order. Please try again.');
                yield put(kitOrdersSlice.actions.setErrorMessage(message));
                yield put(kitOrdersSlice.actions.setState('ERROR'));
                return;
            }

            yield put(kitOrdersSlice.actions.setConfirmation(orderResponse.data));
            yield put(kitOrdersSlice.actions.setState('SUCCESS'));
        }
        catch(e: any){
            yield put(kitOrdersSlice.actions.setErrorMessage('Error placing order. Please try again in a few minutes.'));
            yield put(kitOrdersSlice.actions.setState('ERROR'));
            console.log(`[KitOrdersSagas:createKitOrder] the following exception has occurred`, e);
        }
    }
}
